import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronDown, CheckCircle, XCircle, Clock, FileText, Eye, Shield, TrendingUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;
const statusColors = { draft: "text-gray-400 bg-gray-400/10 border-gray-400/30", under_review: "text-yellow-400 bg-yellow-400/10 border-yellow-400/30", approved: "text-[#22C55E] bg-[#22C55E]/10 border-[#22C55E]/30", rejected: "text-red-400 bg-red-400/10 border-red-400/30", completed: "text-blue-400 bg-blue-400/10 border-blue-400/30" };
function fmtAmount(a, c) { return new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD", minimumFractionDigits: 0 }).format(a); }
function riskColor(s) { return s >= 80 ? "text-[#22C55E]" : s >= 60 ? "text-yellow-400" : "text-red-400"; }
function riskBg(s) { return s >= 80 ? "bg-[#22C55E]/10 border-[#22C55E]/30" : s >= 60 ? "bg-yellow-400/10 border-yellow-400/30" : "bg-red-400/10 border-red-400/30"; }

export default function AdminDeals() {
  const { user } = useAuth();
  const isDemo = user?.is_demo === true;
  const [deals, setDeals] = useState([]);
  const [riskModal, setRiskModal] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const fetchDeals = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/deals`, { withCredentials: true }); setDeals(data); } catch (err) { console.error(err); }
  }, []);
  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const updateStatus = async (id, status) => { await axios.put(`${API}/admin/deals/${id}/status`, { status }, { withCredentials: true }); fetchDeals(); };

  const viewRisk = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/risk-assessment/${id}`, { withCredentials: true });
      setRiskModal(data); setAiResult(null);
    } catch (err) { console.error(err); }
  };

  const runAiAnalysis = async (id) => {
    setAiLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/ai-risk-assessment/${id}`, { withCredentials: true });
      setAiResult(data.ai_analysis);
      if (data.rule_based) setRiskModal(prev => ({ ...prev, ...data.rule_based }));
    } catch (err) { console.error(err); }
    finally { setAiLoading(false); }
  };

  return (
    <div data-testid="admin-deals">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-['Outfit'] text-2xl font-bold text-white">Deal Review</h1>
        <span className="text-[#A1B2A8]/50 text-xs">{deals.length} deals</span>
      </div>
      <div className="space-y-3">
        {deals.map((deal) => (
          <div key={deal.id} className="bg-[#09120D] border border-[#1A2E22] p-6 hover:border-[#22C55E]/30 transition-colors" data-testid={`deal-${deal.id}`}>
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <FileText className="w-4 h-4 text-[#22C55E] flex-shrink-0" />
                  <h3 className="text-white font-['Outfit'] font-semibold text-sm truncate">{deal.products}</h3>
                  <span className={`text-xs capitalize px-2 py-0.5 border flex-shrink-0 ${statusColors[deal.status] || statusColors.draft}`}>{deal.status.replace("_", " ")}</span>
                  {deal.risk_score !== undefined && (
                    <span className={`text-xs px-2 py-0.5 border flex-shrink-0 ${riskBg(deal.risk_score)}`}>
                      <Shield className="w-3 h-3 inline mr-1" />Risk: {deal.risk_score}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
                  <div><p className="text-[#A1B2A8] text-xs">Buyer</p><p className="text-white text-sm">{deal.buyer_name}</p></div>
                  <div><p className="text-[#A1B2A8] text-xs">Supplier</p><p className="text-white text-sm">{deal.supplier_name}</p></div>
                  <div><p className="text-[#A1B2A8] text-xs">Terms</p><p className="text-white text-sm">{deal.quantity} @ {fmtAmount(deal.unit_price, deal.currency)}/MT</p></div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right">
                  <p className="text-[#A1B2A8] text-xs">Total Value</p>
                  <p className="font-['Outfit'] text-xl font-bold text-[#22C55E]">{fmtAmount(deal.total_amount, deal.currency)}</p>
                </div>
                <button onClick={() => viewRisk(deal.id)} className="text-[#A1B2A8] hover:text-white text-xs border border-[#1A2E22] px-3 py-1.5 flex items-center gap-1 hover:border-[#22C55E]/50" data-testid={`risk-btn-${deal.id}`}>
                  <TrendingUp className="w-3 h-3" /> Risk
                </button>
                {isDemo ? (
                  <span className="flex items-center gap-1.5 text-[#22C55E] text-xs border border-[#22C55E]/30 bg-[#22C55E]/5 px-3 py-1.5" data-testid={`deal-readonly-${deal.id}`}>
                    <Eye className="w-3 h-3" /> Read-only
                  </span>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="text-[#A1B2A8] hover:text-white text-xs border border-[#1A2E22] px-3 py-1.5 flex items-center gap-1" data-testid={`deal-action-${deal.id}`}>
                        <Eye className="w-3 h-3" /> <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#09120D] border-[#1A2E22] text-white">
                      <DropdownMenuItem onClick={() => updateStatus(deal.id, "under_review")} className="text-yellow-400 focus:bg-yellow-400/10 cursor-pointer"><Clock className="w-3.5 h-3.5 mr-2" />Under Review</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateStatus(deal.id, "approved")} className="text-[#22C55E] focus:bg-[#22C55E]/10 cursor-pointer"><CheckCircle className="w-3.5 h-3.5 mr-2" />Approve</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateStatus(deal.id, "rejected")} className="text-red-400 focus:bg-red-400/10 cursor-pointer"><XCircle className="w-3.5 h-3.5 mr-2" />Reject</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => updateStatus(deal.id, "completed")} className="text-blue-400 focus:bg-blue-400/10 cursor-pointer"><CheckCircle className="w-3.5 h-3.5 mr-2" />Complete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* IntelliPOS Risk Assessment Modal */}
      <Dialog open={!!riskModal} onOpenChange={() => { setRiskModal(null); setAiResult(null); }}>
        <DialogContent className="bg-[#09120D] border-[#1A2E22] text-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="risk-modal">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">AI Confidence Engine</DialogTitle>
            <DialogDescription className="text-[#A1B2A8]">Commercial intelligence that powers confident decisions.</DialogDescription>
          </DialogHeader>
          {riskModal && (
            <div className="space-y-4 mt-2">
              {/* Rule-based Score */}
              <div className="flex items-center justify-between bg-[#050A07] border border-[#1A2E22] p-4">
                <div><p className="text-[#A1B2A8] text-xs">Confidence Score</p><p className={`font-['Outfit'] text-3xl font-bold ${riskColor(riskModal.risk.score)}`}>{riskModal.risk.score}</p></div>
                <span className={`text-sm font-medium capitalize px-3 py-1 border ${riskModal.risk.level === "low" ? "text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10" : riskModal.risk.level === "medium" ? "text-yellow-400 border-yellow-400/30 bg-yellow-400/10" : "text-red-400 border-red-400/30 bg-red-400/10"}`}>
                  {riskModal.risk.level} risk
                </span>
              </div>
              <div>
                <p className="text-[#A1B2A8] text-xs mb-2 uppercase tracking-wider">Risk Factors</p>
                {riskModal.risk.factors.map((f, i) => (
                  <div key={i} className="flex items-center justify-between border-b border-[#1A2E22] py-2">
                    <div><p className="text-white text-sm">{f.factor}</p><p className="text-[#A1B2A8]/50 text-xs">{f.detail}</p></div>
                    <span className={`text-xs font-mono ${f.impact < 0 ? "text-red-400" : "text-[#22C55E]"}`}>{f.impact > 0 ? "+" : ""}{f.impact}</span>
                  </div>
                ))}
              </div>
              <div className="bg-[#050A07] border border-[#22C55E]/30 p-4">
                <p className="text-[#22C55E] text-xs mb-1 uppercase tracking-wider">Recommended Capital Structure</p>
                <p className="text-white font-['Outfit'] font-semibold">{riskModal.capital_structure_suggestion.recommended}</p>
                <p className="text-[#A1B2A8] text-xs mt-1">{riskModal.capital_structure_suggestion.reason}</p>
                {riskModal.capital_structure_suggestion.alternatives?.length > 0 && (
                  <p className="text-[#A1B2A8]/50 text-xs mt-2">Alternatives: {riskModal.capital_structure_suggestion.alternatives.join(", ")}</p>
                )}
              </div>

              {/* AI Analysis Section */}
              {!aiResult && !aiLoading && (
                <button
                  onClick={() => runAiAnalysis(riskModal.deal_id)}
                  className="w-full bg-[#0D1B13] border border-[#22C55E]/30 text-[#22C55E] hover:bg-[#22C55E]/10 transition-all py-3 font-medium font-['Outfit'] text-sm flex items-center justify-center gap-2"
                  data-testid="run-ai-analysis-btn"
                >
                  <TrendingUp className="w-4 h-4" /> Run AI Confidence Analysis
                </button>
              )}
              {aiLoading && (
                <div className="bg-[#050A07] border border-[#1A2E22] p-6 text-center" data-testid="ai-loading">
                  <div className="w-5 h-5 border-2 border-[#22C55E] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-[#A1B2A8] text-sm">AI Confidence Engine analyzing deal...</p>
                  <p className="text-[#A1B2A8]/40 text-xs mt-1">Evaluating counterparty intelligence, market conditions, and trade risk</p>
                </div>
              )}
              {aiResult && !aiResult.error && (
                <div className="space-y-3" data-testid="ai-analysis-result">
                  <div className="flex items-center gap-2 border-b border-[#22C55E]/20 pb-2">
                    <TrendingUp className="w-4 h-4 text-[#22C55E]" />
                    <span className="text-[#22C55E] text-xs font-medium uppercase tracking-wider">AI Confidence Engine — Deep Analysis</span>
                    {aiResult.confidence && <span className="text-[#A1B2A8]/40 text-xs ml-auto">Confidence: {(aiResult.confidence * 100).toFixed(0)}%</span>}
                  </div>

                  {/* AI Score */}
                  <div className="flex items-center justify-between bg-[#050A07] border border-[#1A2E22] p-4">
                    <div><p className="text-[#A1B2A8] text-xs">AI Confidence Score</p><p className={`font-['Outfit'] text-3xl font-bold ${riskColor(aiResult.overall_score)}`}>{aiResult.overall_score}</p></div>
                    <span className={`text-sm font-medium capitalize px-3 py-1 border ${aiResult.risk_level === "low" ? "text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10" : aiResult.risk_level === "medium" ? "text-yellow-400 border-yellow-400/30 bg-yellow-400/10" : aiResult.risk_level === "high" ? "text-orange-400 border-orange-400/30 bg-orange-400/10" : "text-red-400 border-red-400/30 bg-red-400/10"}`}>
                      {aiResult.risk_level} risk
                    </span>
                  </div>

                  {/* Summary */}
                  <div className="bg-[#050A07] border border-[#1A2E22] p-4">
                    <p className="text-[#A1B2A8] text-xs uppercase tracking-wider mb-2">Executive Summary</p>
                    <p className="text-white text-sm leading-relaxed">{aiResult.summary}</p>
                  </div>

                  {/* Key Risks */}
                  {aiResult.key_risks && aiResult.key_risks.length > 0 && (
                    <div>
                      <p className="text-[#A1B2A8] text-xs uppercase tracking-wider mb-2">Key Risks Identified</p>
                      {aiResult.key_risks.map((r, i) => (
                        <div key={i} className="flex items-start gap-3 border-b border-[#1A2E22] py-2">
                          <span className={`text-xs px-1.5 py-0.5 border mt-0.5 flex-shrink-0 ${r.severity === "high" ? "text-red-400 border-red-400/30 bg-red-400/10" : r.severity === "medium" ? "text-yellow-400 border-yellow-400/30 bg-yellow-400/10" : "text-[#22C55E] border-[#22C55E]/30 bg-[#22C55E]/10"}`}>{r.severity}</span>
                          <div><p className="text-white text-sm">{r.risk}</p><p className="text-[#A1B2A8]/50 text-xs mt-0.5">{r.detail}</p></div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Mitigations */}
                  {aiResult.mitigations && aiResult.mitigations.length > 0 && (
                    <div>
                      <p className="text-[#A1B2A8] text-xs uppercase tracking-wider mb-2">Recommended Mitigations</p>
                      {aiResult.mitigations.map((m, i) => (
                        <div key={i} className="flex items-start gap-2 py-1.5">
                          <CheckCircle className="w-3.5 h-3.5 text-[#22C55E] mt-0.5 flex-shrink-0" />
                          <p className="text-[#A1B2A8] text-sm">{m}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Market Intel + Pricing */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {aiResult.market_intel && (
                      <div className="bg-[#050A07] border border-[#1A2E22] p-4">
                        <p className="text-[#A1B2A8] text-xs uppercase tracking-wider mb-1">Market Intelligence</p>
                        <p className="text-white text-sm">{aiResult.market_intel}</p>
                      </div>
                    )}
                    {aiResult.pricing_assessment && (
                      <div className="bg-[#050A07] border border-[#1A2E22] p-4">
                        <p className="text-[#A1B2A8] text-xs uppercase tracking-wider mb-1">Pricing Assessment</p>
                        <p className="text-white text-sm">{aiResult.pricing_assessment}</p>
                      </div>
                    )}
                  </div>

                  {/* AI Capital Structure Recommendation */}
                  {aiResult.recommended_structure && (
                    <div className="bg-[#050A07] border border-[#22C55E]/30 p-4">
                      <p className="text-[#22C55E] text-xs uppercase tracking-wider mb-1">AI-Recommended Capital Structure</p>
                      <p className="text-white font-['Outfit'] font-semibold">{aiResult.recommended_structure}</p>
                      <p className="text-[#A1B2A8] text-xs mt-1">{aiResult.structure_reason}</p>
                    </div>
                  )}

                  <p className="text-[#A1B2A8]/30 text-[10px] text-right">Powered by Delta GreenX AI Confidence Engine</p>
                </div>
              )}
              {aiResult && aiResult.error && (
                <div className="bg-red-400/5 border border-red-400/20 p-4" data-testid="ai-error">
                  <p className="text-red-400 text-sm">{aiResult.error}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
